import React, {
    MutableRefObject,
    ReactElement, useCallback, useMemo, useRef, useState,
} from "react";
import {
    ActionContextType,
    FormField,
    FormFieldDescriptorInput,
    FormType,
    GetFormDataDocument,
    JobFunction,
    ListRequestTypesDocument,
    Permission,
    useGetFormDataQuery,
    useGetFormQuery, useSaveFormMutation,
    Workflow,
} from "../../../../__generated__/api";
import { removeTypename } from "../../../../api";
import { ReactComponent as WorkflowImage } from "../../../../assets/img/workflow.svg";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import Editor from "../../../../components/agent-v3/editor/Editor";
import DeflectionSource from "../../../../components/pickers/deflection-sources/DeflectionSource";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import Button from "../../../../components/theme/button/Button";
import CallToAction from "../../../../components/theme/call-to-action/CallToAction";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Icon from "../../../../components/theme/icon/Icon";
import SettingsContainer from "../../../../components/theme/settings";
import Toggle from "../../../../components/theme/toggle/Toggle";
import useArticleContext from "../../../../contexts/HelpContext";
import usePortal from "../../../../contexts/PortalContext";
import useRouting, { useParams } from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/session";
import { ErrorRemote } from "../../../error/Error";
import { useSetTitle } from "../base";
import FormDesigner from "./FormDesignerV3";
import { useAutomationFlow } from "../../../../components/workflow-v3/AutomationFlow";
import { ACTION_TYPE_MAP } from "../../../../components/workflow-v3/workflow/nodes";

export const JOB_FUNCTION_NAME = {
    [JobFunction.CustomerSupport]: "Customer Support",
    [JobFunction.CustomerSuccess]: "Customer Success",
    [JobFunction.Engineering]: "Engineering",
    [JobFunction.Finance]: "Finance",
    [JobFunction.HumanResources]: "Human Resources (HR)",
    [JobFunction.InformationTechnology]: "Information Technology (IT)",
    [JobFunction.Legal]: "Legal",
    [JobFunction.Partnerships]: "Partnerships",
    [JobFunction.Product]: "Product",
    [JobFunction.RevenueOperations]: "Revenue Operations",
    [JobFunction.Sales]: "Sales",
    [JobFunction.Founder]: "Executive",
    [JobFunction.Security]: "Security",
    [JobFunction.Other]: "Other",
    [JobFunction.OtherInternal]: "Other",

};

enum Tab {
    REQUEST = "REQUEST",
    FORM = "FORM",
    AUTOMATION = "AUTOMATION"
}

export default function RequestTypes(): ReactElement {
    useArticleContext("urn:fq:article:173ca80539904f9c98277f0c589d2d6e");

    const { Paths, historyPush } = useRouting();
    const { id: portalId } = usePortal();
    const { permissions } = useCurrentSession();

    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const userSession = useCurrentSession();

    const isNew = id === "new" || id === "template";

    const {
        data, error, loading: isFormDataLoading, refetch: refetchForm,
    } = useGetFormQuery({
        skip: id == null,
        fetchPolicy: "no-cache",
        variables: {
            id: isNew ? "urn:fq:form:new" : id,
            hasId: !isNew,
        },
    });

    const isTemplate = permissions.has(Permission.SuperAdmin) && (id === "template" || data?.form?.type === FormType.Template);

    const { data: formData, loading: isDataLoading } = useGetFormDataQuery({
        variables: {
            shouldIncludeTemplateFields: isTemplate,
        },
    });

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .property("name", () => data?.form?.name ?? "")
            .property("description", () => data?.form?.description ?? "")
            .array("tags", () => data?.form?.tags ?? [])
            .array("jobFunctions", () => data?.form?.jobFunctions ?? [])
            .property("iconUrl", () => data?.form?.iconUrl)
            .property("shouldCreateOnOnboarding", () => data?.form?.shouldCreateOnOnboarding)
            .property("isCreatePrivate", () => data?.form?.isCreatePrivate ?? false)
            .property("deflectionIdentity", () => data?.form?.deflection?.identity
                ?? (data?.form?.deflection?.isEnabled ? portalId : undefined))
            .object("workflow", () => data?.form?.workflow)
            .array(
                "userGroupIds",
                () => data?.form?.userGroupIds as string[]
                    ?? userSession.user?.userGroups?.toIdList("id")
                    ?? [],
            )
            .array("fields", () => data?.form?.fields.map(
                field => removeTypename(field) as FormFieldDescriptorInput,
            ) ?? [])
            .property("externalFormId", () => data?.form?.externalFormId),

        [data?.form],
    );
    useSetTitle(properties.name.value || "Request Type", [properties.name.value]);

    const [saveForm] = useSaveFormMutation();
    const [currentTab, setCurrentTab] = useState<Tab>(Tab.REQUEST);

    const performSave = useCallback(async () => {
        const savedForm = await saveForm({
            refetchQueries: [
                { query: GetFormDataDocument },
                { query: ListRequestTypesDocument },
            ],
            variables: {
                id: !isNew ? id : undefined,
                form: {
                    fields: tracker.properties.fields.value.map(field => ({
                        ...field,
                        __typename: undefined,
                    })),
                    type: isTemplate ? FormType.Template : FormType.Default,
                    name: tracker.properties.name.value,
                    description: tracker.properties.description.value,
                    isCreatePrivate: tracker.properties.isCreatePrivate.value,
                    userGroupIds: tracker.properties.userGroupIds.value,
                    tags: tracker.properties.tags.value,
                    iconUrl: tracker.properties.iconUrl.value,
                    shouldCreateOnOnboarding: tracker.properties.shouldCreateOnOnboarding.value,
                    jobFunctions: tracker.properties.jobFunctions.value,
                    workflowId: tracker.properties.workflow.value?.id,
                    deflection: tracker.properties.deflectionIdentity.value ? {
                        isEnabled: tracker.properties.deflectionIdentity.value != null,
                        identity: tracker.properties.deflectionIdentity.value,
                    } : undefined,
                    externalFormId: tracker.properties.externalFormId.value,

                },
            },
        });
        return savedForm.data?.saveForm?.id;
    }, [saveForm, isNew, id, tracker, properties, isTemplate]);

    const dirtyTracker = tracker.useDirtyTracker({
        finishedSavingMessage: `Finished saving "${properties.name.value}".`,
        isDirty: isNew ? true : undefined,
        canReset: !isNew,
        actions: isNew && currentTab !== Tab.REQUEST ? (
            <Button.Back
                variant="outline-blue"
                onClick={() => {
                    if (currentTab === Tab.FORM) {
                        setCurrentTab(Tab.REQUEST);
                    } else if (currentTab === Tab.AUTOMATION) {
                        setCurrentTab(Tab.FORM);
                    }
                }}
            >
                Back
            </Button.Back>
        ) : undefined,
        buttonText: isNew && currentTab !== Tab.AUTOMATION ? (
            <>
                Next
                {" "}
                <Icon.Next />
            </>
        ) : (
            <>
                <Icon.Send />
                {" "}
                Save & Publish
            </>
        ),
        onSave: async (__, onSave) => {
            if (isNew && currentTab === Tab.REQUEST) {
                setCurrentTab(Tab.FORM);
                return;
            }
            if (isNew && currentTab === Tab.FORM) {
                setCurrentTab(Tab.AUTOMATION);
                return;
            }

            const formId = await performSave();

            if (isNew && formId) {
                historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                    setting: "request-forms",
                    id: formId,
                });
            }

            onSave();
        },
    });

    const formRef = useRef<HTMLFormElement>();

    const handleSaveAutomation = useCallback(async () => {
        await performSave();
        await refetchForm();
    }, [performSave, refetchForm]);

    const isLoading = useMemo(() => isFormDataLoading || isDataLoading, [isFormDataLoading, isDataLoading]);

    const actionsContextType = useMemo(() => ACTION_TYPE_MAP[data?.form?.workflow?.steps?.firstElement?.actionId]
    ?? ActionContextType.GenericNoTicket, [data]);

    const { element: automationElement, setShouldShow: setShowAutomation } = useAutomationFlow({
        workflow: data?.form?.workflow as Workflow | undefined,
        title: data?.form?.name,
        onSaved: handleSaveAutomation,
        actionsContextType,
    });

    if (error) {
        return <ErrorRemote error={error} />;
    }

    return (
        <SettingsContainer
            ref={formRef as unknown as MutableRefObject<HTMLDivElement | null>}
            selectedSectionKey={currentTab}
            saveElement={dirtyTracker.element}
            onSectionChanged={tab => {
                if (!formRef.current?.reportValidity()) {
                    throw new Error("Validation error");
                }
                setCurrentTab(tab as Tab);
            }}
            navigation={isNew ? "progress-bar" : "tabs"}
        >
            <SettingsContainer.Section sectionKey={Tab.REQUEST} title="Request Type Details">
                <SettingsContainer.Setting
                    title="Name"
                    description="The title of the request Type displayed to all users that view the request type."
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        type="text"
                        size="small"
                        isRequired
                        value={properties.name.value}
                        onChange={properties.name.setValueFromInput}
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Description"
                    description="The optional description is to help you understand where this request type is used.
                         It will never be displayed to the end user."
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <Editor
                        value={properties.description.value}
                        onChange={properties.description.setValue}
                        size="medium"
                        hasBorder
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Groups"
                    description="Which user groups are allowed to access this request type?"
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <UserGroups
                        value={properties.userGroupIds.value}
                        onSelected={properties.userGroupIds.setValue}
                        isMulti
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Deflection"
                    description="When enabled, Foqal Agent will attempt to use deflection
                        to answer the users question. If not possible, or Foqal Agent can not
                        find a good answer, a standard ticket will be created."
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <DeflectionSource
                        value={properties.deflectionIdentity.value}
                        onSelected={properties.deflectionIdentity.setValue}
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Always Private Create"
                    description="When creating this request type, can the request be posted
                where visible by others, or should it always be private between the
                requester and the agent?"
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <Toggle
                        checked={properties.isCreatePrivate.value}
                        onChange={properties.isCreatePrivate.setCheckedFromInput}
                    />
                </SettingsContainer.Setting>

            </SettingsContainer.Section>
            <SettingsContainer.Section sectionKey={Tab.FORM} title="Form Creation" shouldDisplayTitle={false}>
                <SettingsContainer.Setting
                    title="Form Creation"
                    description="The title of the request Type displayed to all users that view the request type."
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isHideTitle
                    isFullWidth
                >
                    <FormDesigner
                        isLoading={isLoading}
                        fields={properties.fields.value}
                        externalFormId={properties.externalFormId.value}
                        formFields={formData?.formFields as FormField[]}
                        userGroupsIds={properties.userGroupIds.value ?? []}
                        onChange={values => {
                            properties.fields.setValue(values.fields ?? []);
                            properties.externalFormId.setValue(values.externalFormId);
                        }}
                    />
                </SettingsContainer.Setting>
            </SettingsContainer.Section>
            <SettingsContainer.Section sectionKey={Tab.AUTOMATION} title="Automations">
                <CallToAction
                    icon={<Icon.Automation background="warning" variant="warning" size="medium" />}
                    title="Request Created Automation"
                    text="The Automations to execute after the user has created the request.
                        Common actions include routing requests to appropriate people and sending
                        approvals."
                    actions={properties.workflow.value?.steps?.isNotEmpty ? (
                        <Button.Edit
                            variant="outline-gray"
                            onClick={() => setShowAutomation(true)}
                        >
                            Edit
                        </Button.Edit>
                    ) : (
                        <Button.Edit
                            variant="outline-gray"
                            onClick={() => setShowAutomation(true)}
                        >
                            Add Automation
                        </Button.Edit>
                    )}
                    image={(
                        <WorkflowImage
                            style={{ objectFit: "cover", height: 250, transform: "scale(2.5)" }}
                            className="editable-background"
                        />
                    )}
                />
            </SettingsContainer.Section>
            {automationElement}
        </SettingsContainer>
    );
}
